import {
  useAccountBalancesQuery,
  useAccountTypeOrderQuery,
  useAllAccountsQuery,
} from '@transactions/graphql';
import {ArrowUpDown, ChevronDown, ChevronRight, Plus, Search, Star} from 'lucide-react';
import * as React from 'react';
import {Navigate, useSearchParams} from 'react-router-dom';
import {
  indexBalances,
  toSectionAccounts,
} from 'src/AccountsView/accountBalances';
import {
  ACCOUNT_TYPE_OPTIONS,
  isAccountClosed,
  isAccountInactive,
  matchesAccountFilter,
  matchesAccountTypeFilter,
  orderSections,
  parseAccountFilter,
  parseAccountTypeFilter,
  parseExplicitAccountFilter,
  serializeAccountTypeFilter,
} from 'src/AccountsView/accountFilter';
import {AccountRowV2} from 'src/AccountsView/AccountRowV2';
import {AccountSection, SectionCard} from 'src/AccountsView/AccountSection';
import {RearrangeAccountsView} from 'src/AccountsView/RearrangeAccountsView';
import {useCollapsedSections} from 'src/AccountsView/useCollapsedSections';
import {useStarredAccounts} from 'src/AccountsView/useStarredAccounts';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import {FilterDropdown} from 'src/components/FilterDropdown';
import {HeaderActions} from 'src/components/HeaderActions';
import {useModals} from 'src/Modals';
import {PopoverMenu} from 'src/PopoverMenu';
import PopoverMenuItem from 'src/PopoverMenuItem';
import type {AccountListItemFragment} from '@transactions/graphql';
import type {
  AccountFilter,
  AccountTypeFilter,
} from 'src/AccountsView/accountFilter';

const STARRED_SECTION = 'starred';

export const AccountsListView = () => {
  useBreadcrumb({label: 'Accounts', reset: true});
  const [searchParams, setSearchParams] = useSearchParams();
  // The By Connection tab became the standalone /connections page.
  const legacyConnectionsView = searchParams.get('view') === 'connections';
  const typeFilter = parseAccountTypeFilter(searchParams.get('type'));
  // Search lives in the URL like the status/type filters, so it survives
  // navigation (opening an account and hitting back keeps the search).
  const search = searchParams.get('search') ?? '';
  // The status default is contextual: 'active' keeps the plain overview
  // decluttered, but once the user is narrowing — typing a name, picking a
  // type — hidden statuses would make the very account they asked for
  // vanish, so the default widens to 'all'. An explicit pick always wins.
  const explicitFilter = parseExplicitAccountFilter(searchParams.get('filter'));
  const searchNarrows = search.length > 0;
  const defaultFilter: AccountFilter =
    searchNarrows || typeFilter.length > 0 ? 'all' : 'active';
  const filter = explicitFilter ?? defaultFilter;
  // AllAccounts is metadata-only (names, types, logos) so first paint never
  // waits on balance derivation. cache-and-network: render the cached list
  // instantly but always refresh it.
  const {data, loading, refetch} = useAllAccountsQuery({
    fetchPolicy: 'cache-and-network',
  });
  // Balances are the expensive part (derived server-side, changed by
  // raw-transaction mutations that don't touch this query's cache) and
  // stream in after the list renders — rows show a placeholder until then.
  const {data: balancesData, refetch: refetchBalances} =
    useAccountBalancesQuery({fetchPolicy: 'cache-and-network'});
  const {data: typeOrderData, loading: typeOrderLoading} =
    useAccountTypeOrderQuery();
  const {
    starredIds,
    loading: starredLoading,
    toggleStar: handleToggleStar,
  } = useStarredAccounts();
  const {createAccountModal} = useModals();
  const [rearranging, setRearranging] = React.useState(false);
  const {isCollapsed, toggle} = useCollapsedSections('accounts.collapsedSections');
  // Per-section reveal of the folded inactive rows. Ephemeral by design:
  // the fold is the default view, a reveal is a momentary look.
  const [expandedInactive, setExpandedInactive] = React.useState<Set<string>>(
    () => new Set(),
  );
  const toggleInactive = (sectionKey: string) => {
    setExpandedInactive((previous) => {
      const next = new Set(previous);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  const setFilter = (newFilter: AccountFilter) => {
    const next = new URLSearchParams(searchParams);
    // The param is only present for a non-default pick. The default is
    // contextual (see above), so which value gets to be absent depends on
    // whether the view is narrowed — an explicit Active during a search is
    // real state and must be kept.
    if (newFilter === defaultFilter) {
      next.delete('filter');
    } else {
      next.set('filter', newFilter);
    }
    setSearchParams(next);
  };

  const setTypeFilter = (newTypeFilter: AccountTypeFilter) => {
    const next = new URLSearchParams(searchParams);
    const serialized = serializeAccountTypeFilter(newTypeFilter);
    if (serialized == null) {
      next.delete('type');
    } else {
      next.set('type', serialized);
    }
    setSearchParams(next);
  };

  const setSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === '') {
      next.delete('search');
    } else {
      next.set('search', value);
    }
    // replace: the search box updates per keystroke, so pushing would bury the
    // previous page under one history entry per character typed.
    setSearchParams(next, {replace: true});
  };

  const handleRefetch = () => {
    refetch();
    refetchBalances();
  };

  const handleCreate = () => {
    createAccountModal.openModal({onCreate: handleRefetch});
  };

  const handleDoneRearranging = () => {
    setRearranging(false);
    // The rearrange persisted a new global order — refresh allAccounts
    refetch();
  };

  if (legacyConnectionsView) {
    // Only `view` is obsolete — /connections still parses `filter` and
    // `type`, so filtered bookmarks keep their behavior.
    const preserved = new URLSearchParams(searchParams);
    preserved.delete('view');
    const query = preserved.toString();
    return (
      <Navigate
        to={query ? `/connections?${query}` : '/connections'}
        replace
      />
    );
  }

  // cache-and-network reports loading during the background refresh — only
  // show the spinner when there is no cached list to render
  if ((loading && !data) || typeOrderLoading || starredLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-muted-foreground">Loading accounts...</span>
      </div>
    );
  }

  const allAccounts = data?.allAccounts ?? [];
  const balances = indexBalances(balancesData);
  const starredSet = new Set(starredIds);

  // The status filter reads the balance slice (inactive = open + zero
  // balance + no recent activity), which streams in after the metadata —
  // until it lands, every account counts as active and nothing hides. A
  // starred inactive account also stays in the default view: a pin beats
  // the dormancy heuristic.
  const matchesStatus = (
    statusFilter: typeof filter,
    account: AccountListItemFragment,
  ) =>
    matchesAccountFilter(statusFilter, account, balances.get(account._id)) ||
    (statusFilter === 'active' &&
      starredSet.has(account._id) &&
      !isAccountClosed(account));

  // Each dropdown's counts respect the OTHER filter (GitHub-style cross
  // counts: with type=Credit Card selected, "Closed 2" means 2 closed
  // credit cards, and vice versa)
  const typeMatchedAccounts = allAccounts.filter((account) =>
    matchesAccountTypeFilter(typeFilter, account),
  );
  const statusMatchedAccounts = allAccounts.filter((account) =>
    matchesStatus(filter, account),
  );
  const visibleAccounts = typeMatchedAccounts.filter((account) =>
    matchesStatus(filter, account),
  );

  const statusOptions = [
    {
      key: 'active',
      label: 'Active',
      count: typeMatchedAccounts.filter((account) =>
        matchesStatus('active', account),
      ).length,
    },
    {
      key: 'inactive',
      label: 'Inactive',
      count: typeMatchedAccounts.filter((account) =>
        isAccountInactive(account, balances.get(account._id)),
      ).length,
    },
    {
      key: 'closed',
      label: 'Closed',
      count: typeMatchedAccounts.filter(isAccountClosed).length,
    },
  ];
  // Only types with at least one account are offered (plus current
  // selections, so they can always be cleared)
  const typeOptions = ACCOUNT_TYPE_OPTIONS.map((option) => ({
    key: option.key,
    label: option.label,
    group: option.group,
    count: statusMatchedAccounts.filter(option.matches).length,
    present: allAccounts.some(option.matches),
  })).filter((option) => option.present || typeFilter.includes(option.key));
  const typeOrder = typeOrderData?.accountTypeOrder ?? [];

  const isSearching = search.length > 0;
  const accounts = visibleAccounts.filter((account) =>
    account.name.toLowerCase().includes(search.toLowerCase()),
  );

  const accountById = new Map(visibleAccounts.map((a) => [a._id, a]));
  const starredAccounts = starredIds.flatMap((id) => {
    const account = accountById.get(id);
    return account ? [account] : [];
  });
  // One section per depository type (plus the single merged Investments
  // section) that has a visible or folded account, in the persisted section
  // order (unlisted sections follow in default order). In the default
  // 'active' view each section folds its inactive accounts into a
  // disclosure line instead of dropping them.
  const sections = orderSections(typeOrder)
    .map((section) => ({
      section,
      accounts: visibleAccounts.filter(section.matches),
      inactiveAccounts:
        filter === 'active'
          ? typeMatchedAccounts.filter(
              (account) =>
                section.matches(account) &&
                !starredSet.has(account._id) &&
                isAccountInactive(account, balances.get(account._id)),
            )
          : [],
    }))
    .filter(
      ({accounts: sectionAccounts, inactiveAccounts}) =>
        sectionAccounts.length > 0 || inactiveAccounts.length > 0,
    );

  const actions = (
    <Button variant="ghost" size="icon" onClick={handleCreate} aria-label="Add account">
      <Plus className="size-5" />
    </Button>
  );

  const renderReadOnlyRows = (
    rows: AccountListItemFragment[],
    groupLabel?: string,
  ) =>
    rows.map((account) => (
      <AccountRowV2
        key={account._id}
        account={account}
        balance={balances.get(account._id)}
        starred={starredSet.has(account._id)}
        onToggleStar={() => handleToggleStar(account._id)}
        groupLabel={groupLabel}
      />
    ));

  const renderStarredSection = () =>
    starredAccounts.length > 0 && (
      <AccountSection
        label="Starred"
        badges={
          <Star className="h-3.5 w-3.5 shrink-0 fill-amber-500 text-amber-500" />
        }
        accounts={toSectionAccounts(starredAccounts, balances)}
        collapsed={isCollapsed(STARRED_SECTION)}
        onToggle={() => toggle(STARRED_SECTION)}
      >
        {renderReadOnlyRows(starredAccounts)}
      </AccountSection>
    );

  const renderTypeSections = () => (
    <div className="flex flex-col gap-4">
      {renderStarredSection()}
      {sections.map(({section, accounts: sectionAccounts, inactiveAccounts}) => (
        <AccountSection
          key={section.key}
          label={section.label}
          accounts={toSectionAccounts(sectionAccounts, balances)}
          collapsed={isCollapsed(section.key)}
          onToggle={() => toggle(section.key)}
        >
          {renderReadOnlyRows(sectionAccounts, section.label)}
          {inactiveAccounts.length > 0 && (
            <button
              type="button"
              aria-expanded={expandedInactive.has(section.key)}
              onClick={() => toggleInactive(section.key)}
              className="text-muted-foreground hover:text-foreground hover:bg-accent flex items-center gap-1 px-3 py-2.5 text-left text-xs transition-colors sm:px-4"
            >
              {expandedInactive.has(section.key) ? (
                <ChevronDown className="size-3.5" />
              ) : (
                <ChevronRight className="size-3.5" />
              )}
              {inactiveAccounts.length} inactive
            </button>
          )}
          {inactiveAccounts.length > 0 &&
            expandedInactive.has(section.key) &&
            renderReadOnlyRows(inactiveAccounts, section.label)}
        </AccountSection>
      ))}
    </div>
  );

  const renderAllAccountsTab = () => {
    if (allAccounts.length === 0) {
      return (
        <div className="border-border flex flex-col items-center gap-4 rounded-xl border border-dashed p-8">
          <p className="text-muted-foreground">No accounts</p>
          <Button variant="outline" onClick={handleCreate} type="button">
            Add account
          </Button>
        </div>
      );
    }

    if (rearranging) {
      return (
        <RearrangeAccountsView
          accounts={allAccounts}
          balances={balances}
          typeOrder={typeOrder}
          starredIds={starredIds}
          onToggleStar={handleToggleStar}
          isCollapsed={isCollapsed}
          onToggleSection={toggle}
        />
      );
    }

    // Sections can be nonempty with zero visible accounts (every match is
    // folded inactive) — the disclosures must still render so the folded
    // rows stay reachable in place.
    if (visibleAccounts.length === 0 && sections.length === 0) {
      return (
        <div className="text-muted-foreground py-8 text-center text-sm">
          {typeFilter.length === 0
            ? `No ${filter} accounts`
            : 'No matching accounts'}
        </div>
      );
    }

    if (isSearching) {
      // Searching flattens: no section chrome around a handful of hits
      return accounts.length === 0 ? (
        <div className="text-muted-foreground py-8 text-center text-sm">
          No accounts matching "{search}"
        </div>
      ) : (
        <SectionCard>{renderReadOnlyRows(accounts)}</SectionCard>
      );
    }

    return renderTypeSections();
  };

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      {/* The count is the Status filter's own; Add sits in the top bar. */}
      <HeaderActions>{actions}</HeaderActions>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-4 sm:px-6">
        {rearranging ? (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground flex flex-1 items-center gap-2 text-sm">
              <ArrowUpDown className="size-4 shrink-0" />
              Rearranging
            </span>
            <Button variant="primary" onClick={handleDoneRearranging}>
              Done
            </Button>
          </div>
        ) : (
          // One row on desktop; on a phone the search keeps the menu beside
          // it and the filters wrap to their own row.
          <div className="grid grid-cols-[1fr_auto] items-center gap-2 sm:flex">
            <div className="relative min-w-0 sm:flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <input
                type="search"
                placeholder="Search accounts"
                aria-label="Search accounts"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border-border bg-background placeholder:text-muted-foreground focus:ring-primary h-9 w-full rounded-lg border pr-3 pl-9 text-sm focus:ring-2 focus:outline-none"
              />
            </div>
            {allAccounts.length > 0 && (
              <div className="col-span-2 row-start-2 flex items-center gap-2 sm:order-2">
                <FilterDropdown
                  label="Status"
                  allLabel="All"
                  allCount={typeMatchedAccounts.length}
                  value={filter}
                  onChange={(key) => setFilter(parseAccountFilter(key))}
                  options={statusOptions}
                />
                <FilterDropdown
                  label="Type"
                  allLabel="All types"
                  allCount={statusMatchedAccounts.length}
                  multiple
                  value={typeFilter}
                  onChange={setTypeFilter}
                  options={typeOptions}
                />
              </div>
            )}
            {allAccounts.length > 0 && (
              <div className="sm:order-3">
                <PopoverMenu>
                  <PopoverMenuItem
                    iconLeft={<ArrowUpDown />}
                    onSelect={() => {
                      setSearch('');
                      setRearranging(true);
                    }}
                  >
                    Rearrange Accounts
                  </PopoverMenuItem>
                </PopoverMenu>
              </div>
            )}
          </div>
        )}

        {renderAllAccountsTab()}
      </div>
    </div>
  );
};
